/**
 * Análisis temporal de mirada sobre video.
 *
 * Sincronía: el navegador registra, por cada fotograma presentado (requestVideoFrameCallback), el
 * instante esperado de presentación (`performance.now()`) y su tiempo en el video. WebGazer usa el
 * mismo reloj, así que cada muestra se asigna al último fotograma presentado antes de ella.
 *
 * Agregación: igual que en imágenes (mapa normalizado por participante y promedio por grupo),
 * pero restringido a una ventana de tiempo del video. Se puede restar un retraso por participante
 * (la mirada en t refleja lo que se vio en t − retraso).
 */
import { gaussianBlur, histogram, meanGrids, normalizeSum, type Grid } from "./heatmap.js";
import { pointInRect, type RectRoi } from "./roi.js";

export interface PresentedFrame {
  /** Instante esperado de presentación en el reloj `performance.now()` (ms). */
  displayTimeMs: number;
  /** Tiempo del fotograma en el video (ms). */
  mediaTimeMs: number;
}

/**
 * Tiempo del video visible en `perfMs`: el del último fotograma presentado con
 * `displayTimeMs <= perfMs`. `null` si todavía no se había presentado ninguno.
 * `frames` debe estar ordenado por `displayTimeMs` (así lo entrega el navegador).
 */
export function videoTimeAt(frames: ReadonlyArray<PresentedFrame>, perfMs: number): number | null {
  if (frames.length === 0 || !(perfMs >= frames[0]!.displayTimeMs)) return null;
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (frames[mid]!.displayTimeMs <= perfMs) lo = mid;
    else hi = mid - 1;
  }
  return frames[lo]!.mediaTimeMs;
}

/**
 * Índice de fotograma (desde 0) para el tiempo de un fotograma presentado. Los tiempos de
 * presentación son múltiplos de 1/fps redondeados por el navegador (p. ej. 0,033333 s), por eso
 * se redondea al entero más cercano.
 */
export function frameIndex(videoTimeMs: number, fps: number): number {
  return Math.max(0, Math.round((videoTimeMs * fps) / 1000));
}

/** Muestras válidas sobre el video de un participante (arreglos paralelos). */
export interface TimedPoints {
  /** Tiempo del video (ms). */
  t: ArrayLike<number>;
  x: ArrayLike<number>;
  y: ArrayLike<number>;
}

export interface VideoParticipant extends TimedPoints {
  /** Retraso a restar (ms); 0 = sin corrección. */
  lagMs: number;
}

export interface TimeWindow {
  index: number;
  startMs: number;
  endMs: number;
}

/** Ventanas consecutivas `[start, end)` que cubren el video; la última puede ser más corta. */
export function timeWindows(durationMs: number, windowMs: number): TimeWindow[] {
  if (!(durationMs > 0) || !(windowMs > 0)) return [];
  const out: TimeWindow[] = [];
  for (let i = 0, start = 0; start < durationMs; i++, start += windowMs) {
    out.push({ index: i, startMs: start, endMs: Math.min(durationMs, start + windowMs) });
  }
  return out;
}

/** Puntos del participante cuyo tiempo corregido (t − lag) cae en `[startMs, endMs)`. */
export function pointsInWindow(p: VideoParticipant, startMs: number, endMs: number): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < p.t.length; i++) {
    const tc = p.t[i]! - p.lagMs;
    if (tc >= startMs && tc < endMs) out.push({ x: p.x[i]!, y: p.y[i]! });
  }
  return out;
}

export interface WindowHeatmapOptions {
  width: number;
  height: number;
  sigmaFraction: number;
  /** Mínimo de muestras en la ventana para que el participante aporte mapa. */
  minSamples: number;
}

/** Mapa del grupo en una ventana: promedio de mapas individuales normalizados (cada persona pesa igual). */
export function groupWindowHeatmap(
  participants: ReadonlyArray<VideoParticipant>,
  startMs: number,
  endMs: number,
  opts: WindowHeatmapOptions,
): { grid: Grid | null; n: number } {
  const grids: Grid[] = [];
  for (const p of participants) {
    const pts = pointsInWindow(p, startMs, endMs);
    if (pts.length < opts.minSamples) continue;
    const h = histogram(pts, opts.width, opts.height);
    const g = normalizeSum(gaussianBlur(h, opts.sigmaFraction * Math.max(opts.width, opts.height)));
    if (g) grids.push(g);
  }
  return { grid: meanGrids(grids), n: grids.length };
}

export interface SeriesPoint {
  startMs: number;
  endMs: number;
  /** Participantes con datos suficientes en la ventana. */
  n: number;
  mean: number | null;
  /** Intervalo de confianza aproximado al 95 % (media ± 1,96·EE); null con n < 2. */
  ciLow: number | null;
  ciHigh: number | null;
}

function summarize(values: number[]): Pick<SeriesPoint, "n" | "mean" | "ciLow" | "ciHigh"> {
  const n = values.length;
  if (n === 0) return { n, mean: null, ciLow: null, ciHigh: null };
  const m = values.reduce((a, b) => a + b, 0) / n;
  if (n < 2) return { n, mean: m, ciLow: null, ciHigh: null };
  const sd = Math.sqrt(values.reduce((a, v) => a + (v - m) ** 2, 0) / (n - 1));
  const half = (1.96 * sd) / Math.sqrt(n);
  return { n, mean: m, ciLow: Math.max(0, m - half), ciHigh: Math.min(1, m + half) };
}

/**
 * Fracción de mirada dentro de un ROI por ventana: se calcula por participante (sobre sus
 * muestras en la ventana) y se resume por grupo. Fuera del intervalo del ROI el valor es null.
 */
export function roiTimeSeries(
  participants: ReadonlyArray<VideoParticipant>,
  roi: RectRoi & { startMs?: number | null; endMs?: number | null },
  windows: ReadonlyArray<TimeWindow>,
  minSamples: number,
): SeriesPoint[] {
  return windows.map((w) => {
    const active = (roi.startMs == null || w.endMs > roi.startMs) && (roi.endMs == null || w.startMs < roi.endMs);
    if (!active) return { startMs: w.startMs, endMs: w.endMs, n: 0, mean: null, ciLow: null, ciHigh: null };
    const values: number[] = [];
    for (const p of participants) {
      const pts = pointsInWindow(p, w.startMs, w.endMs);
      if (pts.length < minSamples) continue;
      let inside = 0;
      for (const q of pts) if (pointInRect(q.x, q.y, roi)) inside++;
      values.push(inside / pts.length);
    }
    return { startMs: w.startMs, endMs: w.endMs, ...summarize(values) };
  });
}

/** Participantes con datos suficientes en cada ventana (cobertura temporal). */
export function coverageSeries(participants: ReadonlyArray<VideoParticipant>, windows: ReadonlyArray<TimeWindow>, minSamples: number): number[] {
  return windows.map((w) => participants.filter((p) => pointsInWindow(p, w.startMs, w.endMs).length >= minSamples).length);
}

/**
 * Dispersión de la mirada de un grupo en una ventana: distancia media (en unidades normalizadas
 * del video) de la posición media de cada participante al centroide del grupo. Valores bajos =
 * el grupo mira al mismo sitio (sincronía atencional).
 */
export function groupDispersion(participants: ReadonlyArray<VideoParticipant>, startMs: number, endMs: number, minSamples: number): number | null {
  const centers: Array<{ x: number; y: number }> = [];
  for (const p of participants) {
    const pts = pointsInWindow(p, startMs, endMs);
    if (pts.length < minSamples) continue;
    let sx = 0;
    let sy = 0;
    for (const q of pts) {
      sx += q.x;
      sy += q.y;
    }
    centers.push({ x: sx / pts.length, y: sy / pts.length });
  }
  if (centers.length < 2) return null;
  const cx = centers.reduce((a, c) => a + c.x, 0) / centers.length;
  const cy = centers.reduce((a, c) => a + c.y, 0) / centers.length;
  return centers.reduce((a, c) => a + Math.hypot(c.x - cx, c.y - cy), 0) / centers.length;
}
